// Preferred-player preference for the /browse/:type/:id hero Play button.
// The setting (stores/settings.js `preferredPlayer`, edited in SettingsView)
// is a single string: 'auto' | 'putio' | 'torbox' | 'plex:<serverId>'.
//
//   auto           — the historical rule: first Plex match, else Put.io.
//   putio          — the Put.io library copy leads when one exists.
//   plex:<id>      — that specific Plex server's copy leads; other Plex
//                    servers, then Put.io, are the fallback.
//   torbox         — the hero streams the best-ranked torrent via
//                    /play/torbox (movies). The LIBRARY pick below is only
//                    the fallback for when no torrent candidate exists, so
//                    it orders like auto.
//
// Vue-free and DOM-free on purpose: run the suite directly with
//   node --test src/helpers/__tests__/preferred-player.test.js

// Normalize any stored/user value into a tagged descriptor. Junk (wrong
// type, unknown token, empty plex id) degrades to { kind: 'auto' } so a
// tampered localStorage value can never break the hero.
export function parsePreferredPlayer(value) {
  if (typeof value !== "string") return { kind: "auto" };
  const v = value.trim().toLowerCase();
  if (v === "putio") return { kind: "putio" };
  if (v === "torbox") return { kind: "torbox" };
  if (v.startsWith("plex:")) {
    // Preserve the id portion as-is (ids are numeric today, but don't
    // lowercase-mangle a future opaque id) — only trim it.
    const serverId = value.trim().slice("plex:".length).trim();
    if (serverId) return { kind: "plex", serverId };
  }
  return { kind: "auto" };
}

// Canonical storage form for the settings store.
export function sanitizePreferredPlayer(value) {
  const parsed = parsePreferredPlayer(value);
  if (parsed.kind === "plex") return `plex:${parsed.serverId}`;
  return parsed.kind;
}

// The hero's promoted LIBRARY play target, honoring the preference. Returns
// the same tagged descriptor BrowseMediaView's heroPrimaryLibraryMatch always
// produced ({ kind: 'plex'|'putio', match }) or null. Must stay in agreement
// with buildSourceRows' lead selection (browse-source-rows.js) so the hero
// pill and the source picker's "Default" badge point at the same copy.
export function pickHeroLibraryMatch({
  preferred,
  plexMatches = [],
  putioMatch = null,
} = {}) {
  const plex = Array.isArray(plexMatches) ? plexMatches.filter(Boolean) : [];
  const parsed = parsePreferredPlayer(preferred);
  const putioPick =
    putioMatch != null ? { kind: "putio", match: putioMatch } : null;
  const firstPlex = plex.length > 0 ? { kind: "plex", match: plex[0] } : null;

  if (parsed.kind === "putio") return putioPick || firstPlex;
  if (parsed.kind === "plex") {
    const exact = plex.find((m) => String(m?.serverId) === parsed.serverId);
    if (exact) return { kind: "plex", match: exact };
    return firstPlex || putioPick;
  }
  // auto + torbox (library fallback) share the historical ordering.
  return firstPlex || putioPick;
}
